import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  User,
  Mail,
  FileText,
  DollarSign,
  TrendingUp,
  CheckCircle2,
} from "lucide-react";

const STATUS_COLORS = {
  "Mail Sended": "bg-gray-500",
  "Invoice Uploaded": "bg-green-500",
  Troubled: "bg-yellow-600",
  "Change Requested": "bg-red-600",
  Approved: "bg-blue-600",
  Accounting: "bg-purple-600",
  Paid: "bg-emerald-600",
  LUCA: "bg-orange-600",
};

export function CommissionCard(props) {
  const {
    influencerName,
    email,
    invoiceId,
    finalAmount,
    totalEarnings,
    status,
    payment,
    onDetailClick,
    isHorizontal,
  } = props;

  return (
    <Card
      className={cn(
        "group hover:shadow-lg transition-all duration-300 hover:border-primary/50 overflow-hidden",
        isHorizontal && "flex items-center"
      )}
    >
      {/* Gradient accent bar */}
      <div className="h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />

      <div className={cn("flex", isHorizontal ? "flex-row w-full" : "flex-col")}>
        {/* Header */}
        <CardHeader className={cn(isHorizontal && "flex-1")}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg mb-1 group-hover:text-primary transition-colors flex items-center gap-2">
                <User className="h-4 w-4" />
                {influencerName}
              </CardTitle>
              <CardDescription className="flex items-center gap-1.5 text-sm">
                <Mail className="h-3.5 w-3.5" />
                {email}
              </CardDescription>
            </div>
            {status && (
              <Badge
                className={cn(
                  "ml-2",
                  STATUS_COLORS[status] || "bg-gray-500"
                )}
              >
                {status}
              </Badge>
            )}
          </div>
        </CardHeader>

        {/* Content */}
        <CardContent
          className={cn(
            "space-y-3",
            isHorizontal
              ? "flex items-center gap-6 py-6 flex-1"
              : "pt-0"
          )}
        >
          {/* Invoice ID */}
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">
                Invoice ID
              </Label>
              <p className="font-medium text-sm">{invoiceId}</p>
            </div>
          </div>

          {/* Final Amount */}
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
              <DollarSign className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">
                Final Amount
              </Label>
              <p className="font-bold text-lg text-emerald-600 dark:text-emerald-400">
                ${finalAmount?.toFixed(2) || "0.00"}
              </p>
            </div>
          </div>

          {/* Total Earnings (if available) */}
          {totalEarnings !== undefined && totalEarnings > 0 && (
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">
                  Total Earnings
                </Label>
                <p className="font-medium text-sm">
                  ${totalEarnings?.toFixed(2)}
                </p>
              </div>
            </div>
          )}

          {/* Payment Status */}
          {payment && (
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">
                  Payment Invoice
                </Label>
                <p className="font-medium text-sm">{payment.invoiceId}</p>
              </div>
            </div>
          )}
        </CardContent>

        {/* Footer */}
        <CardFooter
          className={cn(
            "pt-4 border-t bg-muted/20",
            isHorizontal && "border-l border-t-0 flex-shrink-0"
          )}
        >
          <Button
            onClick={onDetailClick}
            className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
            variant="outline"
          >
            View Details →
          </Button>
        </CardFooter>
      </div>
    </Card>
  );
}
